import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { RadioButton } from '../common/RadioButton';
import {
  alphabeticalSortOptions,
  AlphabeticalSortOption,
  PublishedAtSortOption,
  publishedAtSortOptions,
} from '../../types/search';
import FullBlockingModal from '../common/FullBlockingModal';

interface JobSortModalProps {
  alphabeticalSort: AlphabeticalSortOption | null;
  publishedAtSort: PublishedAtSortOption | null;
  className?: string;
  isBlocking?: boolean;
  onClose: () => void;
  setAlphabeticalSort: (sort: AlphabeticalSortOption | null) => void;
  setPublishedAtSort: (sort: PublishedAtSortOption | null) => void;
  onSubmit?: () => void;
}

export default function JobSortModal({
  alphabeticalSort,
  publishedAtSort,
  className,
  isBlocking = true,
  onClose,
  setAlphabeticalSort,
  setPublishedAtSort,
  onSubmit,
}: JobSortModalProps) {
  const { t } = useTranslation(['jobs', 'common']);
  const TopDiv = isBlocking ? FullBlockingModal : 'div';

  const handleDateSortChange = (value: PublishedAtSortOption) => {
    setPublishedAtSort(value);
    setAlphabeticalSort(null);
  };

  const handleAlphaSortChange = (value: AlphabeticalSortOption) => {
    setAlphabeticalSort(value);
    setPublishedAtSort(null);
  };

  return (
    <TopDiv className={className}>
      <h3 className="text-secondary mb-4 md:hidden">
        {t('sortBy', { ns: 'jobs', defaultValue: 'Trier par' })}
      </h3>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          {/* Alphabétique */}
          <div>
            <label htmlFor="alphabeticalSort" className="text-secondary">
              {t('alphabeticalSort', {
                ns: 'jobs',
                defaultValue: 'Classement alphabétique',
              })}
            </label>
            <div className="flex my-4 gap-4">
              {alphabeticalSortOptions.map(option => (
                <RadioButton
                  key={option}
                  name="alphabeticalSort"
                  checked={alphabeticalSort === option}
                  onChange={() => handleAlphaSortChange(option)}
                  label={t(`sorts.${option}`, { ns: 'jobs' })}
                  value={option}
                />
              ))}
            </div>
          </div>

          {/* Date de création */}
          <div>
            <label htmlFor="publishedAtSort" className="text-secondary">
              {t('createdAtSort', {
                ns: 'jobs',
                defaultValue: 'Classement par date de création',
              })}
            </label>
            <div className="flex my-4 gap-4">
              {publishedAtSortOptions.map(option => (
                <RadioButton
                  key={option}
                  name="publishedAtSort"
                  checked={publishedAtSort === option}
                  onChange={() => handleDateSortChange(option)}
                  label={t(`sorts.${option}`, { ns: 'jobs' })}
                  value={option}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
          <Button onClick={onSubmit} className="w-full mt-4 md:w-auto md:mt-0 md:px-8">
            {t('showResults', { ns: 'jobs', defaultValue: 'Afficher les résultats' })}
          </Button>

          <button
            className="block font-bold mx-auto text-secondary underline leading-tight md:mx-16"
            onClick={onClose}
          >
            {t('common:form_actions.close')}
          </button>
        </div>
      </div>
    </TopDiv>
  );
}
